import { useCallback } from 'react';

export const useLocalStorage = () => {
  // Garde centralisee: evite les acces au storage hors navigateur (tests/SSR).
  const isStorageAvailable = useCallback(() => {
    return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
  }, []);

  // API historique (string) - conservee pour compatibilite avec l'existant.
  const getItem = useCallback(
    (key: string) => {
      if (!isStorageAvailable()) return null;
      return localStorage.getItem(key);
    },
    [isStorageAvailable]
  );

  // API historique (string) - conservee pour compatibilite avec l'existant.
  const setItem = useCallback(
    (key: string, newValue: string) => {
      if (!isStorageAvailable()) return;
      localStorage.setItem(key, newValue);
    },
    [isStorageAvailable]
  );

  // API historique (string) - conservee pour compatibilite avec l'existant.
  const removeItem = useCallback(
    (key: string) => {
      if (!isStorageAvailable()) return;
      localStorage.removeItem(key);
    },
    [isStorageAvailable]
  );

  // Helper JSON "safe": retourne null si la cle n'existe pas ou si le JSON est invalide.
  const getJsonItem = useCallback(
    <T>(key: string): T | null => {
      const item = getItem(key);
      if (!item) return null;

      try {
        return JSON.parse(item) as T;
      } catch {
        return null;
      }
    },
    [getItem]
  );

  // Helper JSON "safe": serialise automatiquement avant ecriture.
  const setJsonItem = useCallback(
    <T>(key: string, value: T) => {
      setItem(key, JSON.stringify(value));
    },
    [setItem]
  );

  return { getItem, setItem, removeItem, getJsonItem, setJsonItem };
};
